import dreamJob from "@/assets/images/planner/climb-plan/stickers/dream-job.png";
import dreamJobSelected from "@/assets/images/planner/climb-plan/stickers/dream-job-selected.png";
import hardWork from "@/assets/images/planner/climb-plan/stickers/hard-work.png";
import hardWorkSelected from "@/assets/images/planner/climb-plan/stickers/hard-work-selected.png";
import moneyMatters from "@/assets/images/planner/climb-plan/stickers/money-matters.png";
import moneyMattersSelected from "@/assets/images/planner/climb-plan/stickers/money-matters-selected.png";
import possibility from "@/assets/images/planner/climb-plan/stickers/possibility.png";
import possibilitySelected from "@/assets/images/planner/climb-plan/stickers/possibility-selected.png";
import veryMe from "@/assets/images/planner/climb-plan/stickers/very-me.png";
import veryMeSelected from "@/assets/images/planner/climb-plan/stickers/very-me-selected.png";
import adventureAwaits from "@/assets/images/planner/climb-plan/stickers/adventure-awaits.png";
import adventureAwaitsSelected from "@/assets/images/planner/climb-plan/stickers/adventure-awaits-selected.png";
import reachHigh from "@/assets/images/planner/climb-plan/stickers/reach-high.png";
import reachHighSelected from "@/assets/images/planner/climb-plan/stickers/reach-high-selected.png";

/**
 * One "How are you feeling about this plan?" sticker.
 *
 * Two images, because the selected look is art rather than a CSS effect: the
 * yellow border follows each sticker's own die-cut outline. `selectedScale` is
 * how much larger the selected image is drawn than its slot, since the border
 * sits outside the sticker; the slot itself never changes size, so choosing a
 * sticker does not reflow the rows.
 */
export interface ClimbPlanSticker {
  /** Stored on the plan and sent to reporting. Never rename one. */
  id: string;
  /** The words on the sticker, stored beside the id. */
  phrase: string;
  image: string;
  selectedImage: string;
  /** The slot, in px, as drawn in the design. */
  width: number;
  height: number;
  selectedScale: number;
}

/**
 * The stickers, in the order the design lays them out: four on the first row,
 * three on the second.
 *
 * Dream Job, Hard Work, Money Matters, Possibility and Adventure Awaits are
 * the final art, cropped to the sticker from the 600x400 delivery files; each
 * selected image is the same art with its yellow border, which is where
 * `selectedScale` comes from.
 *
 * PLACEHOLDER ART for Very Me and Reach High, from the Figma frame (node
 * 3206:42574). Only Very Me has a real selected image there; Reach High reuses
 * its unselected image as `selectedImage` until the final sticker arrives, so
 * for now it shows selection by opacity alone.
 */
export const CLIMB_PLAN_STICKERS: ClimbPlanSticker[] = [
  {
    id: "DREAM_JOB",
    phrase: "Dream Job",
    image: dreamJob,
    selectedImage: dreamJobSelected,
    width: 285,
    height: 121,
    selectedScale: 1.06,
  },
  {
    id: "HARD_WORK",
    phrase: "Hard Work",
    image: hardWork,
    selectedImage: hardWorkSelected,
    width: 254,
    height: 127,
    selectedScale: 1.06,
  },
  {
    id: "MONEY_MATTERS",
    phrase: "Money Matters",
    image: moneyMatters,
    selectedImage: moneyMattersSelected,
    width: 151,
    height: 152,
    selectedScale: 1.06,
  },
  {
    id: "POSSIBILITY",
    phrase: "Possibility",
    image: possibility,
    selectedImage: possibilitySelected,
    width: 160,
    height: 123,
    selectedScale: 1.05,
  },
  {
    id: "VERY_ME",
    phrase: "Very Me",
    image: veryMe,
    selectedImage: veryMeSelected,
    width: 190,
    height: 181,
    selectedScale: 1.23,
  },
  {
    id: "ADVENTURE_AWAITS",
    phrase: "Adventure Awaits",
    image: adventureAwaits,
    selectedImage: adventureAwaitsSelected,
    width: 175,
    height: 175,
    selectedScale: 1.05,
  },
  {
    id: "REACH_HIGH",
    phrase: "Reach High",
    image: reachHigh,
    selectedImage: reachHighSelected,
    width: 185,
    height: 185,
    selectedScale: 1,
  },
];

/**
 * The stickers these ids name, in the ids' order. An id the list no longer
 * has is left out rather than drawn blank.
 */
export const stickersFor = (ids: string[]): ClimbPlanSticker[] =>
  ids.flatMap((id) => {
    const sticker = CLIMB_PLAN_STICKERS.find((each) => each.id === id);
    return sticker ? [sticker] : [];
  });

/** Where the second row starts. */
export const FIRST_ROW_LENGTH = 4;

/**
 * How the chosen stickers sit in a row on the PDF's first page, spot by spot:
 * the first and last stuck on at an angle, and none quite level with its
 * neighbour, the way stickers pressed on by hand land. The review has its own
 * arrangement, around the Hat's portrait — see ReviewFeelingStickers.
 *
 * `rotate` is in degrees. `offset` is a fraction of the sticker's height,
 * negative for up, so the same arrangement holds at any size.
 */
export const STUCK_ON_SPOTS = [
  { rotate: -13, offset: -0.1 },
  { rotate: 0, offset: 0.19 },
  { rotate: 18, offset: -0.04 },
] as const;
